import config from '../../config/app';
import isBucketedInOptimizelyVariant, { ONBOARDING_EXPERIMENT_THIRD_ARM } from '../../util/optimizely';
import { isProductionEnvironment } from '../../util/environment';

const getFeData = state => state.feData;
const FREEMIUM_STATE = 'freemium';
const TRIAL_STATE = 'trial';
const EXPIRED_STATE = 'expired';
const ACTIVE_STATE = 'active';
const OWNER_ROLE = 'owner';
const ADMIN_ROLE = 'admin';
const RESPONDER_ROLE = 'limited_user';
const MANAGER_ROLE = 'user';
const DISABLE_NURTURE_FEATURE_FLAGS = [
  config.DISABLE_FEATURE_FLAG,
  'test_mode',
  'test_mode_trial',
];
export const ONBOARDING_EXPERIMENT_THIRD_ARM_FEATURE_FLAG = 'onboarding_experiment_third_arm';

// user selectors
export const getUser = state => getFeData(state).user;
export const getUserId = state => getUser(state).id;
export const getUserRole = state => getUser(state).role;
export const getUserFirstName = state => getUser(state).name.split(' ')[0];
const isUserAResponder = state => getUserRole(state) === RESPONDER_ROLE;
const isUserAManager = state => getUserRole(state) === MANAGER_ROLE;
export const isUserAnOwner = state => getUserRole(state) === OWNER_ROLE;
const isUserAnAdmin = state => getUserRole(state) === ADMIN_ROLE;
const getFeatureToggles = state => getUser(state).toggles || [];
export const getContactMethods = state => getUser(state).contactMethods || [];
const isNurtureFeatureToggleOn = state => getFeatureToggles(state).some(
  toggle => toggle.name === config.FEATURE_TOGGLE
);
export const isUserAnOwnerOrAdmin = state => isUserAnAdmin(state) || isUserAnOwner(state);
export const getContactMethodsCount = state => getContactMethods(state).length;
export const isUserAResponderOrManager = state => isUserAResponder(state) || isUserAManager(state);
const getUserCreatedAt = state => getUser(state).createdAt;

// account selectors
export const getAccount = state => getFeData(state).account;
export const getDaysLeftOnTrial = state => getAccount(state).days_left_on_state;
export const getAccountState = state => getAccount(state).state;
export const getAccountId = state => getAccount(state).id;
export const isFreemiumAccountState = state => getAccountState(state) === FREEMIUM_STATE;
export const isTrialAccountState = state => getAccountState(state) === TRIAL_STATE;
export const isExpiredAccountState = state => getAccountState(state) === EXPIRED_STATE;
export const isActiveAccountState = state => getAccountState(state) === ACTIVE_STATE;
export const getUsersCount = state => getAccount(state).users_count;
export const getEscalationPoliciesCount = state => getAccount(state).escalation_policies_count;
export const getSchedulesCount = state => getAccount(state).schedules_count;
export const getFirstEscalationPolicyId = state => getAccount(state).first_ep_id;

// account feature selectors
const getAccountFeatures = state => getFeData(state).accountFeatures;
const getFeatureFlags = state => getAccountFeatures(state).features || [];

export const hasAnyFeatureFlag = (state) => {
  const featureFlagsOnAccount = getFeatureFlags(state);
  return flagsToQuery => (
    flagsToQuery.some(flagToQuery => featureFlagsOnAccount.includes(flagToQuery))
  );
};

const isDisableNurtureFeatureFlagsOn = state => hasAnyFeatureFlag(state)(
  DISABLE_NURTURE_FEATURE_FLAGS
);

// Nurture bar display rules
const shouldHideNurtureBarForExistingCustomers = (state) => {
  const respondersNurtureBarLaunchDate = new Date('2020-07-30T00:00:00Z');
  const userCreatedDate = new Date(getUserCreatedAt(state));
  return isActiveAccountState(state) && (userCreatedDate < respondersNurtureBarLaunchDate);
};
export const userHasValidFeatureGates = state => isNurtureFeatureToggleOn(state)
  && !isDisableNurtureFeatureFlagsOn(state);
export const isUserEligibleForNurtureBar = state => (
  (isUserAnOwnerOrAdmin(state) || isUserAResponderOrManager(state))
  && (isTrialAccountState(state) || isActiveAccountState(state))
  && !shouldHideNurtureBarForExistingCustomers(state)
);
export const shouldSeeVariantOnboarding = (state) => {
  const onboardingExperimentThirdArm = ONBOARDING_EXPERIMENT_THIRD_ARM[
    isProductionEnvironment ? 'production' : 'stagingAndDev'
  ];

  return (
    (hasAnyFeatureFlag(state)([ONBOARDING_EXPERIMENT_THIRD_ARM_FEATURE_FLAG])
      || isBucketedInOptimizelyVariant(
        onboardingExperimentThirdArm.experimentId, onboardingExperimentThirdArm.variantId
      )) && isTrialAccountState(state) && isUserAnOwner(state)
  );
};

// Block the responders and active nurture bar on a feature toggle
const hasRespondersFeatureToggle = state => getFeatureToggles(state).some(
  toggle => toggle.name === 'responders_nurture_bar'
);

export const shouldBlockRespondersAndActives = state => (
  (isUserAResponderOrManager(state) || isActiveAccountState(state))
    && !hasRespondersFeatureToggle(state)
);

// Step Completion Selectors
export const hasCreatedASchedule = state => (
  getSchedulesCount(state) >= 1
);

export default getDaysLeftOnTrial;
